/**
 * Cache Redis des appels sortants coûteux (Apidae).
 *
 * Le cache HTML (`page:*`) ne couvre pas ce besoin : sa clé contient l'hôte, le chemin et
 * 28 paramètres d'URL (`server/cacheKey.js`), alors que la requête Apidae sous-jacente ne
 * dépend, elle, que de l'identifiant demandé. `?query=`, `?categ=`, `?coupon=`… créent donc
 * autant d'entrées HTML — et autant d'appels Apidae — pour une réponse identique, puisque
 * le filtrage se fait côté client.
 *
 * Le client Redis est injecté par le serveur au lieu d'être importé ici : ces modules sont
 * aussi bundlés pour le navigateur, où `ioredis` ne peut pas être résolu. Sans backend
 * (navigateur, ou `setCacheBackend` jamais appelé), tout retombe sur l'appel direct.
 */

export interface CacheBackend {
  get(key: string): Promise<string | null>;
  set(key: string, value: string, ttlSeconds: number): Promise<unknown>;
}

let backend: CacheBackend | null = null;

/** À appeler une fois au démarrage du serveur (cf. `server/index.js`). */
export function setCacheBackend(next: CacheBackend | null) {
  backend = next;
}

const ONE_HOUR_S = 60 * 60;

export function createCache<T>(prefix: string, options?: { ttlSeconds?: number }) {
  const ttlSeconds = options?.ttlSeconds ?? ONE_HOUR_S;
  // Ne retient que des promesses en vol, jamais la donnée : la mémoire du process ne porte
  // que les requêtes en cours, le stockage est entièrement dans Redis.
  const inFlight = new Map<string, Promise<T>>();

  return async (key: string, compute: () => Promise<T>): Promise<T> => {
    const fullKey = `${prefix}:${key}`;

    if (backend) {
      try {
        const raw = await backend.get(fullKey);
        if (raw !== null) return JSON.parse(raw) as T;
      } catch (e) {
        // Redis indisponible ou entrée illisible : on sert quand même la page.
        console.error('[cache] lecture', fullKey, e);
      }
    }

    // Deux rendus concurrents sur la même clé partagent un seul appel réseau.
    const pending = inFlight.get(fullKey);
    if (pending) return pending;

    const task = (async () => {
      const value = await compute();
      // `JSON.stringify(undefined)` ne renvoie pas une chaîne : rien à écrire dans ce cas.
      if (backend && value !== undefined) {
        try {
          await backend.set(fullKey, JSON.stringify(value), ttlSeconds);
        } catch (e) {
          console.error('[cache] écriture', fullKey, e);
        }
      }
      return value;
    })().finally(() => inFlight.delete(fullKey));

    inFlight.set(fullKey, task);
    return task;
  };
}
